import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Container, Row, Col, Card, CardBody, Badge, Button, Input, FormGroup, Label,
} from "reactstrap";
import Header from "../components/Headers/Header";
import AdminNavbar from "../components/Navbars/AdminNavbar/AdminNavbar";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { AuthContext } from "../context/AuthContext";
import { AgendaContext } from "../context/AgendaContext";
import type { AgendaType, EditAgendaType } from "../context/hooks/useAgenda";
import { EditAgendaGerenteModal } from "../components/Agenda/EditAgendaGerenteModal";
import "./HomeGerente.css";

const HomeGerente: React.FC = () => {
    const { objUserTk } = useContext(AuthContext);
    const { listAgenda, getAgendasComRejeicao, agendaPageCount } = useContext(AgendaContext);

    const empresaId: number | undefined = useMemo(
        () => (objUserTk as any)?.EmpresaId,
        [objUserTk]
    );

    const today = useMemo(() => new Date(), []);
    const firstDayOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
    const lastDayOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth() + 1, 0), [today]);

    const [startDate, setStartDate] = useState<string>(firstDayOfMonth.toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState<string>(lastDayOfMonth.toISOString().slice(0, 10));
    const [loadingLocal, setLoadingLocal] = useState<boolean>(false);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(12);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAgenda, setSelectedAgenda] = useState<EditAgendaType | null>(null);

    useEffect(() => {
        applyFilter(1);
    }, [empresaId]);

    function formatDateTime(iso: string) {
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    }

    async function applyFilter(page = pageNumber) {
        if (!empresaId) return;
        setLoadingLocal(true);
        await getAgendasComRejeicao({
            pageNumber: page,
            pageSize,
            empresaId,
            startDate,
            endDate,
        });
        setPageNumber(page);
        setLoadingLocal(false);
    }

    function handleChangePage(next: number) {
        if (next < 1 || (agendaPageCount && next > agendaPageCount)) return;
        applyFilter(next);
    }

    function handleChangePageSize(size: number) {
        setPageSize(size);
        applyFilter(1);
    }

    function mapToEditAgenda(a: AgendaType): EditAgendaType {
        return {
            id: a.id,
            description: a.description,
            location: a.location,
            responsible: a.responsible,
            startDate: a.startDate,
            endDate: a.endDate,
            // cursoId: a.cursoId ?? null,
            beca: a.beca,
            step: a.step,
            tipoAgendaId: a.tipoAgendaId,
            terceiros: a.terceiros ?? [],
        };
    }

    function onCardClick(a: AgendaType) {
        const isPendente = a.step === 1;
        if (!isPendente) return;
        setSelectedAgenda(mapToEditAgenda(a));
        setIsModalOpen(true);
    }

    const totalAgendas = listAgenda?.length ?? 0;
    const periodoTxt = `${new Date(startDate).toLocaleDateString()} a ${new Date(endDate).toLocaleDateString()}`;

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText={"Página Inicial"} search={false} titleSearch={""} />

            <Container className="mt-4" fluid>
                <Card className="shadow border-0 mb-4">
                    <CardBody>
                        <div className="filters-bar">
                            <div className="filters-item">
                                <Label className="form-control-label mb-1">Início</Label>
                                <Input className="form-control-alternative" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>

                            <div className="filters-item">
                                <Label className="form-control-label mb-1">Fim</Label>
                                <Input className="form-control-alternative" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>

                            <div className="filters-item">
                                <Label className="form-control-label mb-1">Itens por página</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    value={pageSize}
                                    onChange={(e) => handleChangePageSize(parseInt(e.target.value || "12", 10))}
                                >
                                    <option value={6}>6</option>
                                    <option value={12}>12</option>
                                    <option value={24}>24</option>
                                    <option value={48}>48</option>
                                </Input>
                            </div>

                            <div className="filters-actions">
                                <Button style={{ background: '#585858', color: '#fff' }} onClick={() => applyFilter(1)} disabled={loadingLocal}>
                                    {loadingLocal ? "Carregando..." : "Aplicar filtro"}
                                </Button>
                            </div>
                        </div>

                        <Row className="mt-3">
                            <Row className="mt-3">
                                <Col>
                                    <div className="meta-bar">
                                        <Badge color="danger" pill className="px-3 py-2">
                                            Agendas com rejeição
                                        </Badge>

                                        <span className="text-muted">
                                            Período: <strong>{periodoTxt}</strong>
                                        </span>

                                        <span className="meta-total">
                                            <strong>{totalAgendas}</strong> {totalAgendas === 1 ? "resultado" : "resultados"}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Row>
                    </CardBody>
                </Card>

                {(!listAgenda || listAgenda.length === 0) && !loadingLocal ? (
                    <Card className="shadow border-0">
                        <CardBody className="text-center py-5">
                            <div className="mb-2">
                                <Badge color="secondary" pill>
                                    Nenhum resultado
                                </Badge>
                            </div>
                            <div className="text-muted">Não encontramos agendas rejeitadas no período selecionado.</div>
                        </CardBody>
                    </Card>
                ) : (
                    <Row className="g-4">
                        {listAgenda.map((a: AgendaType) => {
                            const isPendente = a.step === 1;
                            return (
                                <Col key={a.id} xl="4" lg="6" md="6">
                                    <Card
                                        className={`shadow-sm border-0 h-100 agenda-card ${isPendente ? "clickable" : ""}`}
                                        onClick={() => onCardClick(a)}
                                    >
                                        <CardBody>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h5 className="mb-0 text-dark">{a.description || "Sem descrição"}</h5>
                                                <div className="d-flex gap-2">
                                                    <Badge color="danger" pill>Rejeitada</Badge>
                                                    <Badge color={isPendente ? "warning" : "success"} pill>
                                                        {isPendente ? "Pendente" : "Não pendente"}
                                                    </Badge>
                                                </div>
                                            </div>

                                            <div className="small text-muted mb-3">{a.location || "Local não informado"}</div>

                                            <div className="mb-2">
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 fw-semibold">Início:</span>
                                                    <span>{formatDateTime(a.startDate)}</span>
                                                </div>
                                                <div className="d-flex align-items-center">
                                                    <span className="me-2 fw-semibold">Fim:</span>
                                                    <span>{formatDateTime(a.endDate)}</span>
                                                </div>
                                            </div>

                                            <div className="d-flex flex-wrap gap-2 mt-3">
                                                <Badge color="info" pill>{a.tipoAgendaName || "Tipo não informado"}</Badge>
                                                {a.cursoName ? <Badge color="primary" pill>{a.cursoName}</Badge> : null}
                                                {a.beca ? <Badge color="secondary" pill>Beca: {a.beca}</Badge> : null}
                                            </div>

                                            {!isPendente && (
                                                <div className="mt-3 text-muted small">
                                                    * Somente agendas pendentes podem ser alteradas aqui.
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {agendaPageCount > 1 && (
                    <div className="d-flex align-items-center justify-content-center gap-2 my-4">
                        <Button color="secondary" size="sm" disabled={pageNumber <= 1} onClick={() => handleChangePage(pageNumber - 1)}>
                            ‹ Anterior
                        </Button>
                        <span className="mx-3">
                            Página <strong>{pageNumber}</strong> de <strong>{agendaPageCount}</strong>
                        </span>
                        <Button color="secondary" size="sm" disabled={pageNumber >= agendaPageCount} onClick={() => handleChangePage(pageNumber + 1)}>
                            Próxima ›
                        </Button>
                    </div>
                )}
            </Container>

            <EditAgendaGerenteModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(false)}
                agenda={selectedAgenda}
                onUpdated={() => applyFilter(pageNumber)}
                onDeleted={() => applyFilter(1)}
            />
        </>
    );
};

export default HomeGerente;
